import React, { useCallback, useEffect } from 'react';
import { Drawer, Collapse, Button, Space, Tooltip } from 'antd';
import { ExpandOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAgentChatPanel } from '../../hooks/useAgentChatPanel';
import { useAiPanelStore } from '../../store/aiPanelStore';
import ConversationHistoryList from './ConversationHistoryList';
import AgentChatColumn from './AgentChatColumn';
import AgentCommandPalette from './AgentCommandPalette';
import styles from './AiAssistantPanel.module.css';

/** A command dispatched into the assistant from the global ⌘K palette. */
export type AgentCommand =
  | { type: 'new' }
  | { type: 'prompt'; prompt: string }
  | { type: 'conversation'; id: number };

interface AiAssistantPanelProps {
  open: boolean;
  onClose: () => void;
  /** Command sent from the unified palette; consumed once the panel is open. */
  pendingCommand?: AgentCommand | null;
  onConsumeCommand?: () => void;
}

const AiAssistantPanel: React.FC<AiAssistantPanelProps> = ({
  open,
  onClose,
  pendingCommand,
  onConsumeCommand,
}) => {
  const panel = useAgentChatPanel(open);
  const panelWidth = useAiPanelStore((s) => s.panelWidth);
  const setPanelWidth = useAiPanelStore((s) => s.setPanelWidth);
  const navigate = useNavigate();

  // Run a command handed over by the global palette once the panel is open.
  useEffect(() => {
    if (!open || !pendingCommand) return;
    if (pendingCommand.type === 'new') panel.handleNewChat();
    else if (pendingCommand.type === 'prompt') panel.sendText(pendingCommand.prompt);
    else if (pendingCommand.type === 'conversation') panel.handleSelectConversation(pendingCommand.id);
    onConsumeCommand?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pendingCommand]);

  // Drag-to-resize the panel width (persisted in the store).
  const startResize = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      const onMove = (ev: PointerEvent) => setPanelWidth(window.innerWidth - ev.clientX);
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        document.body.style.userSelect = '';
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      document.body.style.userSelect = 'none';
    },
    [setPanelWidth],
  );

  return (
    <Drawer
      title="AI Асистент"
      placement="right"
      width={panelWidth}
      open={open}
      onClose={onClose}
      destroyOnClose
      extra={
        <Space size={4}>
          <Tooltip title="Команди та пошук розмов">
            <Button
              type="text"
              icon={<ThunderboltOutlined />}
              onClick={() => panel.setPaletteOpen(true)}
              aria-label="Команди"
            />
          </Tooltip>
          <Tooltip title="На весь екран">
            <Button
              type="text"
              icon={<ExpandOutlined />}
              onClick={() => {
                onClose();
                navigate('/ai');
              }}
              aria-label="На весь екран"
            />
          </Tooltip>
        </Space>
      }
      // Non-modal: no backdrop, so the rest of the admin stays clickable while
      // the assistant is open (antd sets the drawer root to pointer-events:none
      // and only the panel itself captures events).
      mask={false}
    >
      {open && (
        <div
          className={styles.resizeHandle}
          style={{ right: panelWidth }}
          onPointerDown={startResize}
          role="separator"
          aria-orientation="vertical"
        />
      )}
      <div className={styles.body}>
        <Collapse
          className={styles.history}
          size="small"
          items={[
            {
              key: 'history',
              label: 'Історія розмов',
              children: (
                <ConversationHistoryList
                  conversations={panel.conversations}
                  activeId={panel.conversationId}
                  onSelect={panel.handleSelectConversation}
                  onNew={panel.handleNewChat}
                  onRename={panel.handleRename}
                  onDelete={panel.handleDelete}
                />
              ),
            },
          ]}
        />

        <div className={styles.messages}>
          <AgentChatColumn panel={panel} />
        </div>
      </div>

      <AgentCommandPalette
        open={panel.paletteOpen}
        onOpenChange={panel.setPaletteOpen}
        conversations={panel.conversations}
        onSelectConversation={panel.handleSelectConversation}
        onRunPrompt={panel.sendText}
        onNewChat={panel.handleNewChat}
      />
    </Drawer>
  );
};

export default AiAssistantPanel;
